/**
 * useDownloadWarRoomPack (P200) -- fetch a signed war-room verdict pack
 * (zip) as a Blob + trigger a browser download. Mirrors
 * useDownloadAnswerPack (manual bearer; bypasses api.request's JSON parse).
 */
import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { currentAuthToken } from "shared/api/client";

export class WarRoomPackDownloadError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
  ) {
    super(detail);
    this.name = "WarRoomPackDownloadError";
  }
}

function _filename(header: string | null, scenarioId: string): string {
  const fallback = `marsad-war-room-${scenarioId}.zip`;
  if (!header) return fallback;
  const match = header.match(/filename="?([^";]+)"?/);
  return match?.[1] ?? fallback;
}

async function _download(vars: {
  scenarioId: string;
  live: boolean;
}): Promise<{ filename: string }> {
  const token = currentAuthToken();
  const url =
    `/v1/scenarios/${encodeURIComponent(vars.scenarioId)}/war-room-pack.zip` +
    (vars.live ? "?live=true" : "");
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/zip",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    let detail = response.statusText || `HTTP ${response.status}`;
    try {
      detail = (await response.json())?.detail ?? detail;
    } catch {
      // non-JSON body; keep statusText
    }
    throw new WarRoomPackDownloadError(response.status, detail);
  }
  const filename = _filename(
    response.headers.get("Content-Disposition"),
    vars.scenarioId,
  );
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
  return { filename };
}

export function useDownloadWarRoomPack(): UseMutationResult<
  { filename: string },
  WarRoomPackDownloadError,
  { scenarioId: string; live: boolean }
> {
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: _download,
  });
}
